import React, { useEffect, useState } from 'react';
import { api, setToken, clearToken, getToken, formatApiError, API } from '../utils/api';
import {
  LogOut, Plus, Trash2, Save, Upload, FileText, RotateCcw,
  FolderKanban, Wrench, Award, Loader2, ExternalLink, BookOpen,
  ArrowUp, ArrowDown, Github,
} from 'lucide-react';
import './Admin.css';

const linesToArr = (s) => s.split('\n').map((x) => x.trim()).filter(Boolean);
const arrToLines = (a) => (a || []).join('\n');

function ReorderButtons({ index, length, onMove }) {
  return <div className="admin-reorder">
    <button className="admin-icon-btn" disabled={index === 0} onClick={() => onMove(index - 1)} title="Move up"><ArrowUp size={16} /></button>
    <button className="admin-icon-btn" disabled={index === length - 1} onClick={() => onMove(index + 1)} title="Move down"><ArrowDown size={16} /></button>
  </div>;
}

/* ---------------- Login ---------------- */
function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/auth/login', { email, password });
      setToken(data.access_token);
      onLogin(data.user);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login-wrap">
      <form className="admin-login-card" onSubmit={submit}>
        <h1 className="admin-login-title">Admin Access</h1>
        <p className="admin-login-sub">Sign in to manage your portfolio content.</p>
        <label className="admin-label">Email</label>
        <input className="admin-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-email" />
        <label className="admin-label">Password</label>
        <input className="admin-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-password" />
        {error && <div className="admin-error" data-testid="admin-login-error">{error}</div>}
        <button className="admin-btn primary full" disabled={loading} data-testid="admin-login-btn">
          {loading ? <Loader2 size={16} className="spin" /> : null} Sign In
        </button>
      </form>
    </div>
  );
}

/* ---------------- Projects ---------------- */
function ProjectsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/content/projects').then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const blank = { title: '', summary: '', tags: [], description: '', focus: [], outcome: '', github: '', year: '' };

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/projects/${editing.id}`, editing);
      else await api.post('/admin/projects', editing);
      setEditing(null);
      await load();
    } catch (e) { alert(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    await api.delete(`/admin/projects/${id}`);
    load();
  };

  const move = async (from, to) => {
    const next = [...items];
    [next[from], next[to]] = [next[to], next[from]];
    setItems(next);
    try { await api.put('/admin/projects/reorder', { ids: next.map((item) => item.id) }); }
    catch (e) { alert(formatApiError(e.response?.data?.detail)); load(); }
  };

  if (editing) {
    return (
      <div className="admin-editor" data-testid="project-editor">
        <h3 className="admin-editor-title">{editing.id ? 'Edit' : 'New'} Project</h3>
        <label className="admin-label">Title</label>
        <input className="admin-input" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
        <label className="admin-label">Year</label>
        <input className="admin-input" value={editing.year} onChange={(e) => setEditing({ ...editing, year: e.target.value })} />
        <label className="admin-label">Summary</label>
        <input className="admin-input" value={editing.summary} onChange={(e) => setEditing({ ...editing, summary: e.target.value })} />
        <label className="admin-label">Description</label>
        <textarea className="admin-textarea" rows="3" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
        <label className="admin-label">Tags (one per line)</label>
        <textarea className="admin-textarea" rows="3" value={arrToLines(editing.tags)} onChange={(e) => setEditing({ ...editing, tags: linesToArr(e.target.value) })} />
        <label className="admin-label">Focus areas (one per line)</label>
        <textarea className="admin-textarea" rows="4" value={arrToLines(editing.focus)} onChange={(e) => setEditing({ ...editing, focus: linesToArr(e.target.value) })} />
        <label className="admin-label">Outcome</label>
        <textarea className="admin-textarea" rows="2" value={editing.outcome} onChange={(e) => setEditing({ ...editing, outcome: e.target.value })} />
        <label className="admin-label">Project link (GitHub / live URL)</label>
        <input className="admin-input" value={editing.github} onChange={(e) => setEditing({ ...editing, github: e.target.value })} placeholder="https://github.com/..." />
        <div className="admin-editor-actions">
          <button className="admin-btn ghost" onClick={() => setEditing(null)}>Cancel</button>
          <button className="admin-btn primary" onClick={save} disabled={saving} data-testid="save-project-btn">
            {saving ? <Loader2 size={16} className="spin" /> : <Save size={16} />} Save
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="admin-tab-head">
        <h2 className="admin-tab-title">Projects ({items.length})</h2>
        <button className="admin-btn primary" onClick={() => setEditing({ ...blank })} data-testid="add-project-btn"><Plus size={16} /> Add Project</button>
      </div>
      <div className="admin-list">
        {items.map((p, index) => (
          <div key={p.id} className="admin-row" data-testid="project-row">
            <div>
              <div className="admin-row-title">{p.title}</div>
              <div className="admin-row-sub">{p.year} · {p.tags?.join(', ')}</div>
            </div>
            <div className="admin-row-actions">
              <ReorderButtons index={index} length={items.length} onMove={(to) => move(index, to)} />
              <button className="admin-icon-btn" onClick={() => setEditing(p)} title="Edit"><Save size={16} /></button>
              <button className="admin-icon-btn danger" onClick={() => remove(p.id)} title="Delete"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Toolkit ---------------- */
function ToolkitTab() {
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get('/content/toolkit').then((r) => setCats(r.data)); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { categories: cats.map((c) => ({ title: c.title, items: c.items })) };
      await api.put('/admin/toolkit', payload);
      alert('Toolkit saved.');
    } catch (e) { alert(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="admin-tab-head">
        <h2 className="admin-tab-title">Technology Toolkit</h2>
        <div>
          <button className="admin-btn ghost" onClick={() => setCats([...cats, { title: 'New Category', items: [] }])}><Plus size={16} /> Category</button>
          <button className="admin-btn primary" onClick={save} disabled={saving} data-testid="save-toolkit-btn">{saving ? <Loader2 size={16} className="spin" /> : <Save size={16} />} Save All</button>
        </div>
      </div>
      <div className="admin-cats">
        {cats.map((c, i) => (
          <div key={i} className="admin-cat-card">
            <div className="admin-cat-head">
              <input className="admin-input" value={c.title} onChange={(e) => { const n = [...cats]; n[i].title = e.target.value; setCats(n); }} />
              <button className="admin-icon-btn danger" onClick={() => setCats(cats.filter((_, idx) => idx !== i))}><Trash2 size={16} /></button>
            </div>
            <label className="admin-label">Items (one per line)</label>
            <textarea className="admin-textarea" rows="5" value={arrToLines(c.items)} onChange={(e) => { const n = [...cats]; n[i].items = linesToArr(e.target.value); setCats(n); }} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Certifications ---------------- */
function CertsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/content/certifications').then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const blank = { name: '', issuer: '', status: 'active', expiry: '', credlyUrl: '' };

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/certifications/${editing.id}`, editing);
      else await api.post('/admin/certifications', editing);
      setEditing(null);
      await load();
    } catch (e) { alert(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this certification?')) return;
    await api.delete(`/admin/certifications/${id}`);
    load();
  };

  const move = async (from, to) => {
    const next = [...items];
    [next[from], next[to]] = [next[to], next[from]];
    setItems(next);
    try { await api.put('/admin/certifications/reorder', { ids: next.map((item) => item.id) }); }
    catch (e) { alert(formatApiError(e.response?.data?.detail)); load(); }
  };

  if (editing) {
    return (
      <div className="admin-editor" data-testid="cert-editor">
        <h3 className="admin-editor-title">{editing.id ? 'Edit' : 'New'} Certification</h3>
        <label className="admin-label">Name</label>
        <input className="admin-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
        <label className="admin-label">Issuer</label>
        <input className="admin-input" value={editing.issuer} onChange={(e) => setEditing({ ...editing, issuer: e.target.value })} />
        <label className="admin-label">Status</label>
        <select className="admin-input" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
          <option value="active">Active</option>
          <option value="inprogress">In Progress</option>
          <option value="inactive">Inactive</option>
        </select>
        <label className="admin-label">Valid to (year, optional)</label>
        <input className="admin-input" value={editing.expiry || ''} onChange={(e) => setEditing({ ...editing, expiry: e.target.value })} />
        <label className="admin-label">Credly verification URL (optional)</label>
        <input className="admin-input" value={editing.credlyUrl || ''} onChange={(e) => setEditing({ ...editing, credlyUrl: e.target.value })} placeholder="https://www.credly.com/badges/..." />
        <div className="admin-editor-actions">
          <button className="admin-btn ghost" onClick={() => setEditing(null)}>Cancel</button>
          <button className="admin-btn primary" onClick={save} disabled={saving} data-testid="save-cert-btn">{saving ? <Loader2 size={16} className="spin" /> : <Save size={16} />} Save</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="admin-tab-head">
        <h2 className="admin-tab-title">Certifications ({items.length})</h2>
        <button className="admin-btn primary" onClick={() => setEditing({ ...blank })} data-testid="add-cert-btn"><Plus size={16} /> Add Certification</button>
      </div>
      <div className="admin-list">
        {items.map((c, index) => (
          <div key={c.id} className="admin-row" data-testid="cert-row">
            <div>
              <div className="admin-row-title">{c.name}</div>
              <div className="admin-row-sub">{c.issuer} · {c.status}{c.credlyUrl ? ' · Credly linked' : ''}</div>
            </div>
            <div className="admin-row-actions">
              <ReorderButtons index={index} length={items.length} onMove={(to) => move(index, to)} />
              <button className="admin-icon-btn" onClick={() => setEditing(c)} title="Edit"><Save size={16} /></button>
              <button className="admin-icon-btn danger" onClick={() => remove(c.id)} title="Delete"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Blogs ---------------- */
function BlogsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/admin/blogs').then((response) => setItems(response.data));
  useEffect(() => { load(); }, []);
  const blank = { title: '', excerpt: '', url: '', thumbnail: '', tags: [], published_at: '', status: 'draft' };

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/blogs/${editing.id}`, editing);
      else await api.post('/admin/blogs', editing);
      setEditing(null);
      await load();
    } catch (error) { alert(formatApiError(error.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const move = async (from, to) => {
    const next = [...items];
    [next[from], next[to]] = [next[to], next[from]];
    setItems(next);
    try { await api.put('/admin/blogs/reorder', { ids: next.map((item) => item.id) }); }
    catch (error) { alert(formatApiError(error.response?.data?.detail)); load(); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this blog entry? The linked article will not be deleted from its publishing platform.')) return;
    await api.delete(`/admin/blogs/${id}`);
    load();
  };

  if (editing) return <div className="admin-editor" data-testid="blog-editor">
    <h3 className="admin-editor-title">{editing.id ? 'Edit' : 'New'} blog entry</h3>
    <p className="admin-help">Add a published article link or save a draft. The external article stays on its original platform.</p>
    <label className="admin-label">Title</label><input className="admin-input" value={editing.title} onChange={(event) => setEditing({ ...editing, title: event.target.value })} required />
    <label className="admin-label">Article URL</label><input className="admin-input" type="url" value={editing.url} onChange={(event) => setEditing({ ...editing, url: event.target.value })} placeholder="https://medium.com/..." required />
    <label className="admin-label">Summary</label><textarea className="admin-textarea" rows="4" value={editing.excerpt} onChange={(event) => setEditing({ ...editing, excerpt: event.target.value })} />
    <label className="admin-label">Thumbnail URL (optional)</label><input className="admin-input" type="url" value={editing.thumbnail || ''} onChange={(event) => setEditing({ ...editing, thumbnail: event.target.value })} placeholder="https://.../image.jpg" />
    <label className="admin-label">Topics (one per line)</label><p className="admin-help">Topics become content filters in the journal, such as Platform Engineering, Security &amp; Compliance, or Automation.</p><textarea className="admin-textarea" rows="3" value={arrToLines(editing.tags)} onChange={(event) => setEditing({ ...editing, tags: linesToArr(event.target.value) })} />
    <label className="admin-label">Published date (optional)</label><input className="admin-input" type="date" value={editing.published_at ? editing.published_at.slice(0, 10) : ''} onChange={(event) => setEditing({ ...editing, published_at: event.target.value ? `${event.target.value}T00:00:00+00:00` : null })} />
    <label className="admin-label">Visibility</label><select className="admin-input" value={editing.status} onChange={(event) => setEditing({ ...editing, status: event.target.value })}><option value="draft">Draft</option><option value="published">Published</option></select>
    <div className="admin-editor-actions"><button className="admin-btn ghost" onClick={() => setEditing(null)}>Cancel</button><button className="admin-btn primary" onClick={save} disabled={saving} data-testid="save-blog-btn">{saving ? <Loader2 size={16} className="spin" /> : <Save size={16} />} Save</button></div>
  </div>;

  return <div>
    <div className="admin-tab-head"><h2 className="admin-tab-title">Blog entries ({items.length})</h2><button className="admin-btn primary" onClick={() => setEditing({ ...blank })} data-testid="add-blog-btn"><Plus size={16} /> Add article</button></div>
    <p className="admin-help">Published entries appear alongside configured feeds. Drafts remain private. Reordering affects only manually managed entries.</p>
    <div className="admin-list">{items.map((item, index) => <div key={item.id} className="admin-row" data-testid="blog-row"><div><div className="admin-row-title">{item.title}</div><div className="admin-row-sub">{item.status === 'published' ? 'Published' : 'Draft'}{item.published_at ? ` · ${item.published_at.slice(0, 10)}` : ''}</div></div><div className="admin-row-actions"><ReorderButtons index={index} length={items.length} onMove={(to) => move(index, to)} /><button className="admin-icon-btn" onClick={() => setEditing(item)} title="Edit"><Save size={16} /></button><button className="admin-icon-btn danger" onClick={() => remove(item.id)} title="Delete"><Trash2 size={16} /></button></div></div>)}</div>
  </div>;
}

/* ---------------- GitHub curation ---------------- */
function GithubTab() {
  const [repos, setRepos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    Promise.all([api.get('/github'), api.get('/admin/github-selection')]).then(([feed, selection]) => {
      const selected = selection.data || [];
      const byUrl = new Map((feed.data.repositories || []).map((repo) => [repo.url, repo]));
      const ordered = selected.map((item) => byUrl.get(item.url)).filter(Boolean);
      const rest = (feed.data.repositories || []).filter((repo) => !selected.some((item) => item.url === repo.url));
      setRepos([...ordered, ...rest]);
    }).catch(() => setNotice('GitHub repositories could not be loaded.'));
  }, []);

  const move = (from, to) => {
    const next = [...repos];
    [next[from], next[to]] = [next[to], next[from]];
    setRepos(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/github-selection', { repos: repos.map(({ url, name }) => ({ url, name })) });
      setNotice('Repository order saved. Newly discovered repositories will continue to appear after this list.');
    } catch (e) { setNotice(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return <div>
    <div className="admin-tab-head"><h2 className="admin-tab-title">GitHub repositories</h2><button className="admin-btn primary" onClick={save} disabled={saving}>{saving ? <Loader2 size={16} className="spin" /> : <Save size={16} />} Save order</button></div>
    <p className="admin-help">Choose the display priority for live GitHub repositories. New repositories remain visible after saved items.</p>
    {notice && <p className="admin-toast">{notice}</p>}
    <div className="admin-list">{repos.map((repo, index) => <div key={repo.url} className="admin-row"><div><div className="admin-row-title">{repo.name}</div><div className="admin-row-sub">{repo.description || 'No description provided.'}</div></div><div className="admin-row-actions"><ReorderButtons index={index} length={repos.length} onMove={(to) => move(index, to)} /></div></div>)}</div>
  </div>;
}

/* ---------------- Resume ---------------- */
function ResumeTab() {
  const [info, setInfo] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/resume/info').then((r) => setInfo(r.data));
  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post('/admin/resume', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFile(null);
      await load();
      alert('Resume updated! The Download button now serves your new file.');
    } catch (e) { alert(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const revert = async () => {
    if (!window.confirm('Revert to the auto-generated resume?')) return;
    await api.delete('/admin/resume');
    load();
  };

  return (
    <div>
      <div className="admin-tab-head"><h2 className="admin-tab-title">Resume</h2></div>
      <div className="admin-resume-card">
        <div className="admin-resume-current">
          <FileText size={20} />
          <div>
            <div className="admin-row-title">Currently serving</div>
            <div className="admin-row-sub" data-testid="resume-current">{info?.filename || '—'}</div>
          </div>
          <a className="admin-btn ghost" href={`${API}/resume/download`} target="_blank" rel="noopener noreferrer"><ExternalLink size={16} /> Preview</a>
        </div>

        <label className="admin-label">Upload new resume (PDF or Word, max 8MB)</label>
        <input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setFile(e.target.files[0])} data-testid="resume-file-input" />
        <div className="admin-editor-actions">
          {info?.custom && <button className="admin-btn ghost" onClick={revert}><RotateCcw size={16} /> Revert to auto-generated</button>}
          <button className="admin-btn primary" onClick={upload} disabled={!file || busy} data-testid="upload-resume-btn">
            {busy ? <Loader2 size={16} className="spin" /> : <Upload size={16} />} Upload
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Dashboard shell ---------------- */
export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState('projects');

  useEffect(() => {
    if (getToken()) {
      api.get('/auth/me').then((r) => setUser(r.data)).catch(() => clearToken()).finally(() => setChecking(false));
    } else {
      setChecking(false);
    }
  }, []);

  const logout = () => { clearToken(); setUser(null); };

  if (checking) return <div className="admin-loading"><Loader2 size={28} className="spin" /></div>;
  if (!user) return <Login onLogin={setUser} />;

  const tabs = [
    { id: 'projects', label: 'Projects', icon: FolderKanban },
    { id: 'toolkit', label: 'Toolkit', icon: Wrench },
    { id: 'certs', label: 'Certifications', icon: Award },
    { id: 'github', label: 'GitHub', icon: Github },
    { id: 'blogs', label: 'Blogs', icon: BookOpen },
    { id: 'resume', label: 'Resume', icon: FileText },
  ];

  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <div className="admin-brand">Arun Bharathwaj</div>
        <div className="admin-brand-sub">Content Studio</div>
        <nav className="admin-nav">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} className={`admin-nav-btn ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}>
                <Icon size={18} /> {t.label}
              </button>
            );
          })}
        </nav>
        <div className="admin-sidebar-foot">
          <a className="admin-nav-btn" href="/" target="_blank" rel="noopener noreferrer"><ExternalLink size={18} /> View site</a>
          <button className="admin-nav-btn" onClick={logout} data-testid="logout-btn"><LogOut size={18} /> Log out</button>
        </div>
      </aside>
      <main className="admin-main">
        {tab === 'projects' && <ProjectsTab />}
        {tab === 'toolkit' && <ToolkitTab />}
        {tab === 'certs' && <CertsTab />}
        {tab === 'github' && <GithubTab />}
        {tab === 'blogs' && <BlogsTab />}
        {tab === 'resume' && <ResumeTab />}
      </main>
    </div>
  );
}
